import React from "react"
import styled, { css, keyframes } from "styled-components"
import { width } from "styled-system"

import { BoxProps } from "components/box"
import { Link, PlainLink as _PlainLink, CTA } from "components/text"
import { textTransform, textDecoration } from "theme"

const spinning = keyframes`
  100% {
    transform: rotate(360deg);
  }
`
const LOADER_SIZE = 30
const LOADER_BORDER_WIDTH = 3
const LOADER_OFFSET = -(LOADER_SIZE + LOADER_BORDER_WIDTH) * 0.5

type ButtonSizing = "sm" | "md" | "lg" | "stretch"
export type ButtonProps = Omit<React.HTMLProps<HTMLButtonElement>, "width"> &
  BoxProps & {
    sizing?: ButtonSizing
    to?: string
    noShadow?: boolean
    bold?: boolean
    loading?: boolean
    isLoading?: boolean
  }

const SIZING_CSS = css<{ sizing?: ButtonSizing }>`
  ${props => {
    switch (props.sizing) {
      case "sm":
        return `padding: ${props.theme.space[4]} ${props.theme.space[4]};`
      case "lg":
        return `padding: ${props.theme.space[4]} ${props.theme.space[8]};`
      case "stretch":
        return `
            width: 100%;
            padding: ${props.theme.space[4]} 0;
            `
      case "md":
      default:
        return `padding: ${props.theme.space[4]} ${props.theme.space[5]};`
    }
  }}
`

const StyledButton = styled(CTA)<ButtonProps>`
  display: inline-block;
  width: fit-content;
  white-space: nowrap;

  ${SIZING_CSS}

  ${props => props.bold && `font-weight: bold;`}

  border-radius: ${props => props.theme.space[1]};
  text-align: center;
  text-decoration: none;
  transition: background-color 0.2s, border-color 0.2s;

  /* If 'isLoading' prop is not passed, the disabled state is greyed out */
  ${props =>
    !props.isLoading &&
    css`
      :disabled {
        cursor: initial;
        opacity: ${props.theme.reducedOpacity};

        background-color: ${props.theme.colors.silver};
        color: ${props.theme.colors.gunmetal};
      }
    `}

  ${props =>
    props.isLoading &&
    css`
      position: relative;
      color: transparent !important; /* Hide text on isLoading state, important needed to override primary/secondary button color */
      pointer-events: none;

      &:before {
        position: absolute;
        margin-top: ${LOADER_OFFSET}px;
        margin-left: ${LOADER_OFFSET}px;
        width: ${LOADER_SIZE}px;
        height: ${LOADER_SIZE}px;
        left: 50%;
        top: 50%;
        content: "";
        border: transparent ${LOADER_BORDER_WIDTH}px solid;
        border-radius: 50%;
        border-top-color: ${props.theme.colors.evergreen}; /* Sets the color of the spinner */

        animation: ${spinning} 750ms linear infinite;
      }
    `}

  ${textTransform}
  ${textDecoration}
  ${width}
`

StyledButton.defaultProps = {
  type: "submit",
  sizing: "md",
  bold: true
}

const NarrowLink = styled(Link)<{ sizing: ButtonSizing }>`
  width: fit-content;

  /* Button CSS takes care of the hover state */
  :hover {
    opacity: 1;
  }

  ${props => props.sizing === "stretch" && `width: -moz-available; width: -webkit-fill-available;`}

  button {
    ${SIZING_CSS}
  }
`

const PlainLink = styled(_PlainLink)`
  /* Button CSS takes care of the hover state */
  :hover {
    opacity: 1;
  }
`

/*
  If 'to' prop is passed, behaves as a link from react-router
  If 'href' prop is passed, behaves as <a> html tag
  Else behaves as a simple <button> tag
*/
function Button({ to, href, children, alignSelf, loading, ...props }: ButtonProps) {
  return to ? (
    <NarrowLink to={to} alignSelf={alignSelf} sizing={props.sizing}>
      <StyledButton isLoading={loading} {...props}>
        {children}
      </StyledButton>
    </NarrowLink>
  ) : href ? (
    <PlainLink href={href}>
      <StyledButton isLoading={loading} {...props}>
        {children}
      </StyledButton>
    </PlainLink>
  ) : (
    <StyledButton isLoading={loading} {...props}>
      {children}
    </StyledButton>
  )
}

export default Button
